import * as React from 'react'
import { CircleSlashIcon, LoaderCircleIcon, ShieldAlertIcon, SproutIcon } from 'lucide-react'

import { cn } from '@/components/new/lib/utils'

function Card({ className, size = 'default', ...props }: React.ComponentProps<'div'> & { size?: 'default' | 'sm' }) {
    return (
        <div
            data-slot="card"
            data-size={size}
            className={cn(
                'group/card bg-card text-card-foreground shadow-xs ring-foreground/10 *:[img:first-child]:rounded-t-xl *:[img:last-child]:rounded-b-xl flex flex-col gap-6 overflow-hidden rounded-xl py-6 text-sm ring-1 has-[>img:first-child]:pt-0 data-[size=sm]:gap-4 data-[size=sm]:py-4',
                className
            )}
            {...props}
        />
    )
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-header"
            className={cn(
                'group/card-header @container/card-header has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto] [.border-b]:pb-6 group-data-[size=sm]/card:[.border-b]:pb-4 grid auto-rows-min items-start gap-1 rounded-t-xl px-6 group-data-[size=sm]/card:px-4',
                className
            )}
            {...props}
        />
    )
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-title"
            className={cn(
                'font-heading text-base font-medium leading-normal group-data-[size=sm]/card:text-sm',
                className
            )}
            {...props}
        />
    )
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
    return <div data-slot="card-description" className={cn('text-muted-foreground text-sm', className)} {...props} />
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-action"
            className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
            {...props}
        />
    )
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
    return <div data-slot="card-content" className={cn('px-6 group-data-[size=sm]/card:px-4', className)} {...props} />
}

function CardLoaderContent({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-loader-content"
            role="status"
            aria-busy="true"
            className={cn('text-muted-foreground flex min-h-40 flex-1 items-center justify-center py-10', className)}
            {...props}
        >
            <LoaderCircleIcon className="size-5 animate-spin" aria-hidden="true" />
        </div>
    )
}

type CardStatusContentProps = Omit<React.ComponentProps<'div'>, 'title'> & {
    status: 'noAccess' | 'error' | 'empty'
    title: React.ReactNode
    description: React.ReactNode
}

const cardStatusIconMap = {
    noAccess: ShieldAlertIcon,
    error: CircleSlashIcon,
    empty: SproutIcon,
}

const cardStatusIconClassNameMap = {
    noAccess: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300',
    error: 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300',
    empty: 'bg-muted text-muted-foreground',
}

function CardStatusContent({ status, title, description, className, children, ...props }: CardStatusContentProps) {
    const Icon = cardStatusIconMap[status]

    return (
        <div
            data-slot="card-status-content"
            data-status={status}
            className={cn(
                'flex min-h-40 flex-1 flex-col items-center justify-center gap-3 py-10 text-center',
                className
            )}
            {...props}
        >
            <div
                className={cn(
                    'flex size-10 items-center justify-center rounded-full',
                    cardStatusIconClassNameMap[status]
                )}
            >
                <Icon className="size-4" aria-hidden="true" />
            </div>
            <div className="space-y-1">
                <div className={cn('text-sm font-medium', status === 'empty' && 'text-muted-foreground')}>
                    {title}
                </div>
                {description ? <div className="text-muted-foreground max-w-sm text-sm">{description}</div> : null}
            </div>
            {children}
        </div>
    )
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-footer"
            className={cn(
                '[.border-t]:pt-6 group-data-[size=sm]/card:[.border-t]:pt-4 flex items-center rounded-b-xl px-6 group-data-[size=sm]/card:px-4',
                className
            )}
            {...props}
        />
    )
}

export {
    Card,
    CardHeader,
    CardFooter,
    CardTitle,
    CardAction,
    CardDescription,
    CardContent,
    CardLoaderContent,
    CardStatusContent,
}
